import { documentStore, singularityStore } from "@/store/store";
import {
  generatePageInTheBackground,
  rewriteGlobalBlockInTheBackground
} from "./background-page-generation";
import { triggerSsrRefreshForElementContainingGlobalBlock } from "@/lib/singularity/page-structure/api-for-global-block-inline-editing";
import { TreeNode } from "@/types/tree";
import {
  FUTURELAYER_FOOTER_ELEMENT_TYPE,
  FUTURELAYER_HEADER_ELEMENT_TYPE
} from "../headers-footers/headers-footers";
import { treeNodeReduce } from "@/lib/tree/tree";
import lodash from "lodash";
import { aiJobController } from "@/futurelayer/ai-job-manager/ai-jobs";

class BackgroundPageGenerationService {
  private generatingPages = new Set<number>();
  private generatingBlocks = new Set<number>();

  async startBackgroundGenerationForPage(
    pageTitle: string,
    pageId: number
  ): Promise<void> {
    if (this.isGenerating(pageId)) {
      return;
    }

    this.setGenerating(pageId, true);

    aiJobController.addJob(
      async (abortController) => {
        try {
          await generatePageInTheBackground(pageTitle, pageId, abortController);
        } catch (error) {
          console.error("Background page generation failed:", error);
        } finally {
          this.setGenerating(pageId, false);
        }
      },
      {
        jobType: 'background_page_generation',
        pageId,
        pageTitle,
        description: `Generating page: ${pageTitle}`,
        priority: 'low'
      }
    );
  }

  async startBackgroundGenerationForGlobalBlock(
    blockId: number,
    getContainingElementId: () => TreeNode["id"] | null
  ): Promise<void> {
    if (this.isGenerating(blockId)) {
      return;
    }

    console.log("ASDFG startBackgroundGenerationForGlobalBlock");

    aiJobController.addJob(
      async (abortController) => {
        this.setGeneratingBlock(blockId, true);
        try {
          await rewriteGlobalBlockInTheBackground(blockId, abortController);
        } catch (error) {
          console.error("ASDFG Background global block generation failed:", error);
        } finally {
          this.setGeneratingBlock(blockId, false);
          const containingElementId = getContainingElementId();
          if (containingElementId) {
            console.log("triggering ssr refresh for ", containingElementId);
            triggerSsrRefreshForElementContainingGlobalBlock(containingElementId);
          }
        }
      },
      {
        jobType: 'background_block_generation',
        blockId,
        blockTitle: `Block ${blockId}`,
        description: `Generating global block: ${blockId}`,
        priority: 'high'
      }
    );
  }

  isGenerating(pageId: number): boolean {
    return this.generatingPages.has(pageId);
  }

  getGeneratingPageIds(): number[] {
    return Array.from(this.generatingPages);
  }

  getGeneratingBlockIds(): number[] {
    return Array.from(this.generatingBlocks);
  }

  private setGenerating(pageId: number, generating: boolean): void {
    if (generating) {
      this.generatingPages.add(pageId);
    } else {
      this.generatingPages.delete(pageId);
    }

    const updatedPages = singularityStore.pagesInWP.map(page => {
      if (page.id === pageId) {
        return { ...page, generatingInBackground: generating };
      }
      return page;
    });

    singularityStore.setPagesInWP(updatedPages);
  }

  private setGeneratingBlock(blockId: number, generating: boolean): void {
    if (generating) {
      this.generatingBlocks.add(blockId);
    } else {
      this.generatingBlocks.delete(blockId);
    }

    const elementIdsOfBackgroundGeneratingHeadersAndFooters =
      findHeaderAndFooterElementIdsInCurrentDocumentThatUseSpecifiedBlockIds(
        Array.from(this.generatingBlocks)
      );

    console.log("ASDFG setGeneratingBlock called for", blockId, generating);
    console.log(
      "ASDFG idsOfBackgroundGeneratingHeadersAndFooters",
      elementIdsOfBackgroundGeneratingHeadersAndFooters
    );

    singularityStore.setBackgroundGeneratingHeadersAndFooters(
      elementIdsOfBackgroundGeneratingHeadersAndFooters
    );
  }
}

function findHeaderAndFooterElementIdsInCurrentDocumentThatUseSpecifiedBlockIds(
  blockIds: number[]
): TreeNode["id"][] {
  const headerAndFooterElementIds: TreeNode["id"][] = [];

  treeNodeReduce(
    (node: TreeNode, acc: TreeNode["id"][]) => {
      if (
        node.data.type === FUTURELAYER_HEADER_ELEMENT_TYPE ||
        node.data.type === FUTURELAYER_FOOTER_ELEMENT_TYPE
      ) {
        const blockId = lodash.get(
          node.data.properties,
          "content.content.block"
        );

        if (blockIds.includes(blockId)) {
          acc.push(node.id);
        }
      }
      return acc;
    },
    documentStore.document.tree.root,
    headerAndFooterElementIds
  );

  return headerAndFooterElementIds;
}

export function addElementIdToBackgroundGeneratingHeadersAndFootersInStore(newId: TreeNode['id']) {
  const currentlyBackgroundGeneratingIds = new Set<TreeNode['id']>(singularityStore.backgroundGeneratingHeadersAndFooters);
  currentlyBackgroundGeneratingIds.add(newId);
  singularityStore.setBackgroundGeneratingHeadersAndFooters(Array.from(currentlyBackgroundGeneratingIds));
}

export const backgroundPageGenerationService =
  new BackgroundPageGenerationService();
